import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../../ui';

export default function NavigationMenu({ id, label, icon, sections, active, open, onOpenChange }) {
  const location = useLocation();
  const [position, setPosition] = useState({});
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const initialFocus = useRef(0);
  const selected = sections.findIndex(section => section.to === location.pathname);
  const host = buttonRef.current?.closest('.d2-top');


  useLayoutEffect(() => {
    if (!open || !host) return;
    // Render outside the horizontally scrolling tabs, retaining the shell theme.
    const updatePosition = () => {
      const button = buttonRef.current.getBoundingClientRect();
      const container = host.getBoundingClientRect();
      setPosition({
        left: Math.max(8, Math.min(button.left, window.innerWidth - (menuRef.current?.offsetWidth || 184) - 8)) - container.left,
        top: button.bottom - container.top + 4,
        maxHeight: Math.max(0, Math.min(320, window.innerHeight - button.bottom - 12)),
      });
    };
    updatePosition();
    menuRef.current?.querySelectorAll('[role="menuitem"]')[initialFocus.current]?.focus();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, host]);

  useEffect(() => {
    if (!open) return;
    const outside = event => {
      if (!buttonRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) onOpenChange(false);
    };
    const escape = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onOpenChange(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open, onOpenChange]);

  const show = index => {
    initialFocus.current = index;
    onOpenChange(true);
  };
  const onMenuKey = event => {
    const items = Array.from(menuRef.current.querySelectorAll('[role="menuitem"]'));
    const index = items.indexOf(document.activeElement);
    let next;
    if (event.key === 'ArrowDown') next = (index + 1) % items.length;
    if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = items.length - 1;
    if (next !== undefined) {
      event.preventDefault();
      items[next].focus();
    } else if (event.key === 'Tab') {
      const tabs = Array.from(document.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]'))
        .filter(element => element.tabIndex >= 0 && !menuRef.current.contains(element) && !element.hidden
          && window.getComputedStyle(element).display !== 'none' && window.getComputedStyle(element).visibility !== 'hidden');
      const nextTab = tabs[tabs.indexOf(buttonRef.current) + (event.shiftKey ? -1 : 1)];
      if (nextTab) event.preventDefault();
      // At the navigation boundary, let the browser advance into page content.
      (nextTab || buttonRef.current).focus();
      onOpenChange(false);
    } else if (event.key === ' ') {
      event.preventDefault();
      items[index]?.click();
    }
  };

  return <>
    <button
      ref={buttonRef} type="button" id={`${id}-trigger`}
      className={`d2-tab d2-tab-cyber-bulletin${active ? ' on' : ''}`}
      aria-haspopup="menu" aria-expanded={open}
      aria-controls={open ? `${id}-menu` : undefined}
      onClick={() => open ? onOpenChange(false) : show(Math.max(0, selected))}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          show(event.key === 'ArrowUp' ? sections.length - 1 : 0);
        }
      }}
    >
      <Icon name={icon} size="sm" />
      {label}
      <Icon name="chevronDown" size={12} aria-hidden="true" />
    </button>
    {open && host && createPortal(
      <div ref={menuRef} id={`${id}-menu`} role="menu"
        aria-labelledby={`${id}-trigger`} className="d2-bulletin-menu"
        style={position} onKeyDown={onMenuKey}>
        {sections.map((section, index) => (
          <Link key={section.key} to={section.to} role="menuitem" tabIndex={-1}
            aria-current={index === selected ? 'page' : undefined}
            className="d2-bulletin-menu-item"
            onClick={() => { onOpenChange(false); buttonRef.current?.focus(); }}>
            {section.label}
          </Link>
        ))}
      </div>, host,
    )}
  </>;
}
